import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { intl } from "LocaleWrapper";

import { ColumnSettingsProps, ContentProps, FilterList, Sizes, SortList } from "@cofano/cck";
import { GroupsProps } from "@cofano/cck/Filter/FilterGroups";
import { FilteredValuesProps } from "@cofano/cck/Filter/Filter";

import { TableId, useTableProvider, useSettingsProvider } from "components/table";

import { usePermission } from "imports/Utils";

import { SortProperty } from "__generated__/graphql";
import { SortDirection } from "__generated__/enums";

type UseBackendTableProps = {
	id: TableId;
	defaultSize?: keyof typeof Sizes;
	defaultPageSize?: number;
	defaultParamsFilter?: FilterList;
	defaultFilter?: FilterList;
	onChange?: (
		pagination: { page: number, size: number },
		sort: Array<SortProperty> | undefined,
		//eslint-disable-next-line @typescript-eslint/no-explicit-any
		filter: Record<string, any>,
		callback: (props: { content: ContentProps, totalResults: number }) => void,
	) => void;
	selectSettings?: {
		selectionEnabled?: boolean;
		isFullSelectEnabled: boolean,
		disabled?: (row: { id?: number }) => boolean
	};
	onSelect?: (rows: Array<{ id?: number }>, isAllSelected: boolean) => void;
	refreshInterval?: number;
	disableParams?: boolean;
	saveForOrganizationEnabled?: boolean;
}

type StateProps = {
	pagination: {
		currentPage: number;
		pageSize: number;
	},
	content: ContentProps;
	contentLoading: boolean;
	totalResults: number;
	filters: FilterList;
	sort?: SortList;
}

const useBackendTable = ({
	id,
	defaultSize,
	defaultPageSize,
	defaultParamsFilter,
	defaultFilter,
	onChange,
	selectSettings,
	onSelect,
	refreshInterval,
	disableParams = false,
	saveForOrganizationEnabled = true,
}: UseBackendTableProps) => {

	const tableId = `cckTable_${id}`;
	const filterId = `cckFilter_${id}`;

	const { tableSettingsProvider, filterSettingsProvider } = useSettingsProvider();

	const tablePreferences = tableSettingsProvider.settings(tableId);
	const filterPreferences = filterSettingsProvider.settings(filterId);

	const { openDialog } = useTableProvider();

	const { isAdmin, isCompanyUser } = usePermission();

	const getDefaultFilters = useCallback(() => {
		if (searchParams.get("filters") && !disableParams) {
			return JSON.parse(searchParams.get("filters") ?? "");
		} else if (defaultParamsFilter) {
			return defaultParamsFilter;
		} else if (filterPreferences?.groups && filterPreferences.selectedGroupIndex) {

			const fields = filterPreferences.groups[filterPreferences.selectedGroupIndex]?.fields || [];

			return fields.filter((item) => item.visible && item.value !== undefined && item.value !== null).map((item) => ({
				property: item.id,
				value: item.value,
			}));
		} else if (filterPreferences?.fields) {

			const fields = filterPreferences.fields;

			return fields.filter((item) => item.visible && item.value !== undefined && item.value !== null).map((item) => ({
				property: item.id,
				value: item.value,
			}));
		} else {
			return defaultFilter || [];
		}
	}, []);

	const [ searchParams, setSearchParams ] = useSearchParams();

	const [ state, setState ] = useState<StateProps>({
		pagination: {
			currentPage: parseInt(String(searchParams.get("currentPage") ?? 0)),
			pageSize: parseInt(String(searchParams.get("pageSize") ?? tablePreferences?.pageSize ?? defaultPageSize ?? 10)),
		},
		content: [],
		contentLoading: true,
		totalResults: 0,
		filters: getDefaultFilters(),
		sort: searchParams.get("sort") ? JSON.parse(searchParams.get("sort") ?? "") : tablePreferences?.sort,
	});

	const [ filterGroups, setFilterGroups ] = useState<{ groups: GroupsProps | undefined, selectedGroupIndex: number }>({
		groups: undefined,
		selectedGroupIndex: 0,
	});

	const tableRef = useRef<{
		getSelection?: () => { selection: [], isFullSelect: boolean },
		clearSelection?: () => void,
		handleSelectPageOnChange?: (checked: boolean, rows: Array<{ id?: number }>) => void,
	}>();

	const filterRef = useRef<{
		handleGroupOnChange?: (index: number) => void,
	}>();

	const stateRef = useRef(state);

	const getFilterObject = (filters: FilterList) => {
		return filters.reduce((obj, item) => {
			return Object.assign(obj, { [ item.property ]: typeof item.value === "string" ? item.value.trim() : item.value });
		}, {});
	};

	const handleOnChange = (pagination: { currentPage: number, pageSize: number }, sort: SortList | undefined, filters: FilterList) => {
		if (onChange) {
			setState(oldState => ({
				...oldState,
				contentLoading: true,
			}));

			const paginationObject = {
				page: pagination.currentPage,
				size: pagination.pageSize,
			};

			const filterObject = getFilterObject(filters);

			onChange(
				paginationObject,
				sort?.map(it => ({
					field: it.property,
					direction: it.direction === "ASC" ? SortDirection.Asc : SortDirection.Desc,
				})),
				filterObject,
				(result) => {
					setState(oldState => ({
						...oldState,
						content: result.content,
						contentLoading: false,
						totalResults: result.totalResults,
					}));
				},
			);
		}
	};

	const handleNewFilter = useCallback((newFilter: FilterList) => {
		setState(oldState => ({
			...oldState,
			filters: newFilter,
			pagination: {
				currentPage: 0,
				pageSize: oldState.pagination.pageSize,
			},
		}));
	}, []);

	const refetch = (newPage?: number) => {
		if ((newPage !== undefined) && (newPage !== stateRef.current.pagination.currentPage)) {
			setState({
				...stateRef.current,
				pagination: {
					currentPage: newPage,
					pageSize: stateRef.current.pagination.pageSize,
				},
			});
		} else {
			handleOnChange(stateRef.current.pagination, stateRef.current.sort, stateRef.current.filters);
		}
	};

	const refetchAfterDelete = useCallback(() => {
		const isLastOnPage = stateRef.current.content.length === 1 && (stateRef.current.pagination.currentPage > 0);
		refetch(isLastOnPage ? stateRef.current.pagination.currentPage - 1 : undefined);
	}, []);

	const handleParams = () => {
		const currentParams = new URLSearchParams(window.location.search);

		const newPage = String(state.pagination.currentPage);
		const newSize = String(state.pagination.pageSize);
		const newSort = state.sort ? JSON.stringify(state.sort) : null;
		const newFilters = state.filters.length > 0 ? JSON.stringify(state.filters) : null;

		const hasChanged =
			currentParams.get("currentPage") !== newPage ||
			currentParams.get("pageSize") !== newSize ||
			currentParams.get("sort") !== newSort ||
			currentParams.get("filters") !== newFilters;

		if (hasChanged) {
			setSearchParams(
				(prev) => {
					const latestParams = new URLSearchParams(prev);
					latestParams.set("currentPage", newPage);
					latestParams.set("pageSize", newSize);

					if (newSort) {
						latestParams.set("sort", newSort);
					} else {
						latestParams.delete("sort");
					}

					if (newFilters) {
						latestParams.set("filters", newFilters);
					} else {
						latestParams.delete("filters");
					}

					return latestParams;
				},
				{ replace: true },
			);
		}
	};

	useEffect(() => {
		handleOnChange(state.pagination, state.sort, state.filters);

		if (!disableParams) {
			handleParams();
		}

		if (!refreshInterval) {
			return;
		}

		const intervalId = setInterval(() => {
			handleOnChange(state.pagination, state.sort, state.filters);
		}, refreshInterval);

		return () => {
			if (intervalId) {

				clearInterval(intervalId);
			}
		};
	}, [ state.pagination.currentPage, state.pagination.pageSize, state.sort, state.filters ]);

	useEffect(() => {
		stateRef.current = state;
	}, [ state ]);

	return {
		tableProps: {
			id: tableId,
			tableRef: tableRef,
			defaultSize: tablePreferences?.size || defaultSize || "NORMAL",
			settingsProvider: tableSettingsProvider,

			paginationSettings: {
				page: state.pagination.currentPage,
				size: state.pagination.pageSize,
				goToPage: (newCurrentPage: number, newPageSize: number) => {
					setState(oldState => ({
						...oldState,
						pagination: {
							currentPage: newCurrentPage,
							pageSize: newPageSize,
						},
					}));
				},
				totalResults: state.totalResults,
				isLastOnPage: state.content.length === 1 && (state.pagination.currentPage > 0),
			},

			selectSettings: (selectSettings) && {
				onSelect: (selectedRows: Array<{ id?: number }>, isAllSelected: boolean) => {
					if (onSelect) {
						onSelect(selectedRows, isAllSelected);
					}
				},
				isFullSelectEnabled: (typeof selectSettings.isFullSelectEnabled === "boolean") ? selectSettings.isFullSelectEnabled : true,
				selectionEnabled: (typeof selectSettings.selectionEnabled === "boolean") ? selectSettings.selectionEnabled : true,
				disabled: selectSettings.disabled,
			},

			sortSettings: {
				type: "MULTIPLE" as const,
				sort: state.sort,
				setSort: (newSort?: SortList) => {
					setState(oldState => ({
						...oldState,
						sort: newSort,
					}));
				},
			},

			filterSettings: {
				onFilter: (newValues: FilterList) => {
					if (tableRef.current?.clearSelection) {
						tableRef.current.clearSelection();
					}

					handleNewFilter(newValues);
				},
				controlledValues: state.filters as Array<{ property: string; value: unknown }>,
			},

			optionalSaveOptions:
				saveForOrganizationEnabled && (isAdmin || isCompanyUser) ?
					[
						{
							label: intl.formatMessage({ id: "SAVE_FOR_ORGANIZATION" }),
							onClick: (_: string, settings: ColumnSettingsProps[]) => openDialog(id, settings),
						},
					]
					:	undefined,


			content: state.content,
			contentLoading: state.contentLoading,
		},

		filterProps: {
			id: filterId,
			filterRef: filterRef,
			settingsProvider: filterSettingsProvider,
			onSearch: (newValues: FilterList) => {
				if (tableRef.current?.clearSelection) {
					tableRef.current.clearSelection();
				}

				handleNewFilter(newValues);
			},
			onChange: (groups: GroupsProps, selectedGroupIndex: number) => {
				setFilterGroups({
					groups: groups,
					selectedGroupIndex: selectedGroupIndex,
				});
			},
			controlledValues: state.filters as FilteredValuesProps,
		},

		filterValuesProps: {
			values: state.filters as FilteredValuesProps,
			onDelete: (property: string) => {
				const newFilter = [ ...stateRef.current.filters ].filter(item => item.property !== property);

				if (tableRef.current?.clearSelection) {
					tableRef.current.clearSelection();
				}

				handleNewFilter(newFilter);
			},
			groups: filterGroups.groups,
			selectedGroupIndex: filterGroups.selectedGroupIndex,
			onGroupChange: (option?: { index: number }) => {
				if (filterRef.current?.handleGroupOnChange) {
					filterRef.current.handleGroupOnChange(option ? option.index : 0);
				}
			},
		},

		refetch: (newPage?: number) => {
			if (tableRef.current?.clearSelection) {
				tableRef.current.clearSelection();
			}

			refetch(newPage);
		},

		refetchAfterDelete: () => {
			if (tableRef.current?.clearSelection) {
				tableRef.current.clearSelection();
			}

			refetchAfterDelete();
		},

		clearSelection: () => {
			if (tableRef.current?.clearSelection) {
				tableRef.current.clearSelection();
			}
		},

		handleSelectPageOnChange: (checked: boolean, rows: Array<{ id?: number }>) => {
			if (tableRef.current?.handleSelectPageOnChange) {
				tableRef.current.handleSelectPageOnChange(checked, rows);
			}
		},

		setFilters: handleNewFilter,

		filters: state.filters,
		filterObject: getFilterObject(state.filters),
	};
};

export default useBackendTable;
